import React from "react";
import {ForeignKey, Model, isEmpty, formatDate} from "weed-js";
import {DutchieAccount} from "./dutchie.js";
import {Product} from "./dutchie.js";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import UndoIcon from "@mui/icons-material/Undo";
import Tooltip from "@mui/material/Tooltip";


export class LabModel extends Model {

    static namespace = "lab";
    static dal = (client) => client.lab.dal.v1();
    static accountsToParam(accounts) {
        return {
            integration: "dutchie",
            account_id__in: accounts.reduce(
                (previousValue, currentValue) => {
                    return [...previousValue, currentValue.id];
                }, []).join(",")
        };
    }

    static getParams(startDate, endDate, selectedIntegrationAccounts) {
        return {
            ...this.getBaseParams(startDate, endDate),
            ...LabModel.accountsToParam(selectedIntegrationAccounts)
        };
    }
}

export class ProductCluster extends LabModel {
    static columns = ["id", "name", "integration", "account_id", "central_product_id"];
    static uri = "/product-clusters/";
    static pluralName = "product-clusters";
    static dateField = "created_at";
    static foreignKeys = {
        account: new ForeignKey(DutchieAccount),
        central_product: new ForeignKey(Product),
    };

    static formatDate(date) {
        return date.toISOString().split("T")[0];
    }
}

export class ProductClusterMember extends LabModel {
    static columns = ["id", "account_id", "product_id", "category_name", "cluster_id"];
    static uri = "/product-cluster-members/";
    static pluralName = "product-cluster-members";
    static dateField = "created_at";
    static foreignKeys = {
        account: new ForeignKey(DutchieAccount),
        product: new ForeignKey(Product),
    };

    static formatDate(date) {
        return date.toISOString().split("T")[0];
    }
}

export class ProductVelocity extends LabModel {
    static columns = [
        "id", "integration", "account_id",
        "product_id", "velocity", "product_name", "category_name",
        "created_at", "stddev", "mean", "histogram",
    ];
    static uri = "/product-velocities/";
    static pluralName = "product-velocities";
    static dateField = "created_at";
    static foreignKeys = {
        account: new ForeignKey(DutchieAccount),
        product: new ForeignKey(Product),
    };

    static formatDate(date) {
        return date.toISOString().split("T")[0];
    }

    toRow(organizationId) {
        return [
            this.category_name,
            <a key={"product-link-" + this.product_id} href={`/analytics/organizations/${organizationId}/integration/dutchie/product/${this.product_id}`}>{this.product_name}</a>,
            this.velocity,
            this.margin,
            this.mean,
            this.stddev,
        ];
    }

    static getParams(startDate, endDate, selectedIntegrationAccounts) {
        return {
            [this.dateField + "__gte"]: startDate,
            [this.dateField + "__lte"]: endDate,
            proj: this.columns.join(","),
            flat: true,
            limit: 1000000000,
            _cb: 1,
            ...LabModel.accountsToParam(selectedIntegrationAccounts)
        };
    }
}

export class InventoryRecommendation extends LabModel {
    static columns = [
        "account_id",
        "adopted_at",
        "created_at",
        "discontinued_product_id",
        "discontinued_product_margin",
        "discontinued_product_velocity",
        "discontinued_runway",
        "discontinued_units_on_hand",
        "dismissed_at",
        "id",
        "integration",
        "recommended_product_id",
        "recommended_product_margin",
        "recommended_product_runway",
        "recommended_product_velocity",
        "recommended_units_on_hand",
        "score",
        "viewed"
    ];
    static uri = "/inventory-recommendations/";
    static pluralName = "inventory_recommendations";
    static dateField = "created_at";
    static foreignKeys = {
        discontinued_product: new ForeignKey(Product),
        recommended_product: new ForeignKey(Product),
        account: new ForeignKey(DutchieAccount)
    };

    getStatus() {
        if (!isEmpty(this.adopted_at) && !isEmpty(this.unadopted_at)) {
            return "Abandoned";
        } else if (!isEmpty(this.adopted_at) && isEmpty(this.unadopted_at)) {
            return "Adopted";
        } else if (isEmpty(this.adopted_at) && isEmpty(this.unadopted_at)) {
            return "Unadopted";
        }
    }

    getIcon(status) {
        switch(status) {
        case "Adopted":
            return <Tooltip title="Great job! You're following this recommendation."><CheckCircleIcon style={{color: "green"}} /></Tooltip>;
        case "Abandoned":
            return <Tooltip title="You followed this recommendation for a while, then stopped."><CancelIcon style={{color: "yellow"}} /></Tooltip>;
        case "Unadopted":
            return <Tooltip title="You have not yet followed this recommendation"><UndoIcon style={{color: "red"}} /></Tooltip>;
        }
    }

    getBoostIcon(boost) {
        if (boost > 0) {
            return <span style={{color: "green"}}>↑ {boost.toFixed(2)}%</span>;
        } else if (boost < 0) {
            return <span style={{color: "red"}}>↓ {Math.abs(boost).toFixed(2)}%</span>;
        }
        return <span>---</span>;
    }

    toFloat(value) {
        if (isEmpty(value)) {
            return 0.0;
        } else if (typeof value === "string") {
            return parseFloat(value.replace(/[^0-9.-]+/g, ""));
        }
        return parseFloat(value);
    }

    toInt(value) {
        if (isEmpty(value)) {
            return 0;
        } else if (typeof value === "string") {
            return parseInt(value.replace(/[^0-9.-]+/g, ""));
        }
        return parseInt(value);
    }

    toRow() {
        const productInstances = Product.byId;
        if (isEmpty(productInstances)) {
            return null;
        }

        const dutchieAccounts = DutchieAccount.byId;

        this.discontinued_product = productInstances.get(this.discontinued_product_id);
        this.recommended_product = productInstances.get(this.recommended_product_id);
        this.account = dutchieAccounts.get(this.account_id);

        console.log("InventoryRecommendation.toRow: this =", this);

        this.recommended_product_margin = this.toFloat(this.recommended_product_margin);
        this.discontinued_product_margin = this.toFloat(this.discontinued_product_margin);
        this.recommended_product_velocity = this.toFloat(this.recommended_product_velocity);
        this.discontinued_product_velocity = this.toFloat(this.discontinued_product_velocity);
        this.discontinued_units_on_hand = this.toInt(this.discontinued_units_on_hand);
        this.recommended_units_on_hand = this.toInt(this.recommended_units_on_hand);
        this.score = this.toFloat(this.score);

        const organizationId = this.account?.organization_id;
        const discontinuedProductLink = (!isEmpty(this.discontinued_product) ?
            <a href={`/analytics/organizations/${organizationId}/integration/dutchie/product/${this.discontinued_product_id}`}>{this.discontinued_product.name}</a> : "---");
        const recommendedProductLink = (!isEmpty(this.recommended_product) ?
            <a href={`/analytics/organizations/${organizationId}/integration/dutchie/product/${this.recommended_product_id}`}>{this.recommended_product.name}</a> : "---");
        const status = this.getStatus();
        const statusIcon = this.getIcon(status);

        const marginBoost = (this.recommended_product_margin - this.discontinued_product_margin) / this.discontinued_product_margin * 100;
        const velocityBoost = (this.recommended_product_velocity - this.discontinued_product_velocity) / this.discontinued_product_velocity * 100;

        return [
            this.id,
            statusIcon,
            discontinuedProductLink,
            this.toInt(this.discontinued_units_on_hand),
            recommendedProductLink,
            this.toInt(this.recommended_units_on_hand),
            this.getBoostIcon(marginBoost),
            this.getBoostIcon(velocityBoost),
            this.toFloat(this.score)
        ];
    }
}